import { useState } from 'react';
import axios from 'axios';
import { TID } from '@/lib/testIds';
import { Mail, Phone, MapPin, ArrowUpRight, Loader2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: '', email: '', phone: '', company: '', message: '' };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ loading: false, ok: false, error: '' });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return 'Please enter a valid email address.';
    if (!form.message.trim() || form.message.trim().length < 10)
      return 'Please share a message of at least 10 characters.';
    return '';
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setStatus({ loading: false, ok: false, error: err });
      return;
    }
    setStatus({ loading: true, ok: false, error: '' });
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/contact`, payload);
      setStatus({ loading: false, ok: true, error: '' });
      setForm(initial);
    } catch (e2) {
      const detail =
        e2?.response?.data?.detail ||
        'Something went wrong sending your message. Please try again.';
      setStatus({
        loading: false,
        ok: false,
        error: typeof detail === 'string' ? detail : 'Unable to send message.',
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid={TID.contact.root}
      className="relative py-24 md:py-32 border-t border-white/8 overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-56 -left-40 w-[700px] h-[700px] ember-glow opacity-60"
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-14">
          {/* Left */}
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow mb-8">Get in Touch</p>
            <h2
              className="font-display font-semibold text-text tracking-tight leading-[1.02]"
              style={{ fontSize: 'clamp(38px, 5.4vw, 76px)' }}
            >
              Let&apos;s build a business ready for its next stage of{' '}
              <span className="text-ember">growth.</span>
            </h2>
            <p className="mt-8 max-w-lg text-[15px] leading-relaxed text-text/80">
              Whether you&apos;re scaling operations, restructuring teams,
              preparing for expansion, or driving transformation — I&apos;d
              be glad to explore how we can work together.
            </p>

            <ul className="mt-10 space-y-4">
              <InfoRow
                testid={TID.contact.infoEmail}
                icon={<Mail size={16} />}
                label="Email"
                value="me@sejalpatil.com"
                href="mailto:me@sejalpatil.com"
              />
              <InfoRow
                testid={TID.contact.infoPhone}
                icon={<Phone size={16} />}
                label="Phone"
                value="+91 99677 62310"
                href="tel:+919967762310"
              />
              <InfoRow
                testid={TID.contact.infoLocation}
                icon={<MapPin size={16} />}
                label="Location"
                value="Mumbai, India"
              />
            </ul>
          </div>

          {/* Form */}
          <form
            data-testid={TID.contact.form}
            onSubmit={onSubmit}
            noValidate
            className="col-span-12 md:col-span-7 md:pl-8 grid grid-cols-2 gap-x-6 gap-y-6 rounded-3xl border border-white/8 bg-ink-2 p-6 md:p-10"
          >
            <Field
              label="Name"
              name="name"
              testid={TID.contact.name}
              value={form.name}
              onChange={onChange}
              placeholder="Your full name"
              required
            />
            <Field
              label="Email"
              name="email"
              type="email"
              testid={TID.contact.email}
              value={form.email}
              onChange={onChange}
              placeholder="you@company.com"
              required
            />
            <Field
              label="Phone"
              name="phone"
              testid={TID.contact.phone}
              value={form.phone}
              onChange={onChange}
              placeholder="+91"
            />
            <Field
              label="Company"
              name="company"
              testid={TID.contact.company}
              value={form.company}
              onChange={onChange}
              placeholder="Organisation"
            />

            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.28em] text-text-70 mb-2">
                Message
              </label>
              <textarea
                name="message"
                data-testid={TID.contact.message}
                value={form.message}
                onChange={onChange}
                rows={4}
                placeholder="Tell me about your business and what you're looking to solve…"
                className="line-input resize-none min-h-[110px]"
                required
              />
            </div>

            <div className="col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-2">
              <button
                type="submit"
                data-testid={TID.contact.submit}
                disabled={status.loading}
                className="btn-ember rounded-full px-7 py-3.5 text-[12px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {status.loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Schedule a Free Call <ArrowUpRight size={14} />
                  </>
                )}
              </button>
              <p className="text-[11px] uppercase tracking-[0.22em] text-text-70">
                Response within 48 hours
              </p>
            </div>

            {status.ok && (
              <p
                data-testid={TID.contact.success}
                className="col-span-2 flex items-center gap-2 text-ember text-sm"
              >
                <Send size={14} /> Thank you — your message has been received. I&apos;ll be in touch shortly.
              </p>
            )}
            {status.error && (
              <p
                data-testid={TID.contact.error}
                className="col-span-2 text-sm text-red-400"
              >
                {status.error}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = 'text', value, onChange, placeholder, testid, required }) {
  return (
    <div className="col-span-2 sm:col-span-1">
      <label className="block text-[10px] uppercase tracking-[0.28em] text-text-70 mb-2">
        {label}
        {required && <span className="text-ember"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="line-input"
        autoComplete="off"
      />
    </div>
  );
}

function InfoRow({ icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <span className="mt-1 text-ember">{icon}</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-text-70">
          {label}
        </div>
        <div className="font-display font-semibold text-text text-lg group-hover:text-ember transition-colors">
          {value}
        </div>
      </div>
    </div>
  );
  return (
    <li data-testid={testid}>
      {href ? (
        <a href={href} className="block">
          {inner}
        </a>
      ) : (
        inner
      )}
    </li>
  );
}
